import type { ColumnDef, EnemyDef, LevelDef, PickupDef, SegmentDef } from '../../types';

// OP-203 Las Fincas (GDD §10.4, §10.2, issue #112): the evening approach to the end of world 2, written by hand with
// the format of §15.6. 400 u of approach at 6.0 u/s (5.5 of world 2 plus 0.5 of level 3; takeoff, warm-up 18 %,
// tension 26 % up to CP-203-A at x 200, respite 15 %, peak 41 %), CP-203-B 5 u before the arena, the arena of
// JEFE-2 (open length: the fight ends it), the US sky of 120 u (New York) with bonus bills and the jump zone.
// Columns: gap 3.7 u every 8.5 u, the gap centre never moves more than 3.0 u (the world allows 3.5). Hazards of the
// approach: the generic ones of world 2 (gallinazos EN-01, the search bloc EN-02, paragliders EN-04 and the seagull
// flocks EN-23, announced with INTERCEPT), without the crane of the port (EN-21) nor the antenna of the city
// (EN-22). An armor before the checkpoint and a bribe in the respite; no scapular (it goes in OP-202). All x are local.

const column = (x: number, gapY: number): ColumnDef => ({ x, gapY, gapH: 3.7, skin: 'w2-stables' });
const dollar = (x: number, y: number): PickupDef => ({ x, y, type: 'dollar' });
const armor = (x: number, y: number): PickupDef => ({ x, y, type: 'armor' });
const bribe = (x: number, y: number): PickupDef => ({ x, y, type: 'bribe' });
const gallinazo = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-01' });
const paraglider = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-04' });
/** A helicopter of the search bloc, hovering high. */
const searchBloc = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-02' });
/** A flock of three seagulls in a V (the leader is at x, y). */
const gull = (x: number, y: number): EnemyDef => ({ x, y, type: 'EN-23' });

const takeoff: SegmentDef = { id: 'w2-fincas-takeoff-01', kind: 'takeoff', length: 40, columns: [], enemies: [], pickups: [] };

const warmup01: SegmentDef = {
  id: 'w2-fincas-warmup-01',
  kind: 'pattern',
  phase: 'warmup',
  length: 66,
  columns: [
    column(20.0, 6.0),
    column(28.5, 6.5),
    column(37.0, 6.0),
    column(45.5, 5.5),
    column(54.0, 6.0),
    column(62.5, 6.5),
  ],
  enemies: [gallinazo(30.0, 10.5), gallinazo(60.0, 2.5)],
  pickups: [
    dollar(31.75, 6.0),
    dollar(32.75, 6.3),
    dollar(33.75, 6.0),
    dollar(57.25, 6.0),
    dollar(58.25, 6.3),
    dollar(59.25, 6.0),
  ],
};

const tension01: SegmentDef = {
  id: 'w2-fincas-tension-01',
  kind: 'pattern',
  phase: 'tension',
  length: 47,
  columns: [
    column(5.0, 7.0),
    column(13.5, 5.5),
    column(22.0, 7.5),
    column(30.5, 6.0),
    column(39.0, 8.0),
  ],
  enemies: [paraglider(24.0, 11.5), gull(40.0, 10.0)],
  pickups: [
    dollar(8.25, 6.0),
    dollar(9.25, 6.3),
    dollar(10.25, 6.0),
    dollar(33.75, 6.75),
    dollar(34.75, 7.05),
    dollar(35.75, 6.75),
  ],
};

const tension02: SegmentDef = {
  id: 'w2-fincas-tension-02',
  kind: 'pattern',
  phase: 'tension',
  length: 47,
  columns: [
    column(0.5, 5.5),
    column(9.0, 7.0),
    column(17.5, 7.0),
    column(26.0, 5.5),
    column(34.5, 7.5),
    column(43.0, 6.0),
  ],
  enemies: [gallinazo(14.0, 2.5), paraglider(36.0, 11.5)],
  pickups: [
    dollar(12.25, 6.75),
    dollar(13.25, 7.05),
    dollar(14.25, 6.75),
    dollar(29.25, 6.25),
    dollar(30.25, 6.55),
    dollar(31.25, 6.25),
    armor(21.75, 6.25),
  ],
};

const respite01: SegmentDef = {
  id: 'w2-fincas-respite-01',
  kind: 'pattern',
  phase: 'respite',
  length: 54,
  columns: [
    column(4.5, 6.0),
    column(13.0, 6.5),
    column(21.5, 6.0),
    column(30.0, 6.5),
    column(38.5, 6.0),
    column(47.0, 6.5),
  ],
  enemies: [],
  pickups: [
    dollar(7.75, 6.0),
    dollar(8.75, 6.3),
    dollar(9.75, 6.0),
    dollar(33.25, 6.0),
    dollar(34.25, 6.3),
    dollar(35.25, 6.0),
    bribe(17.25, 6.25),
  ],
};

const peak01: SegmentDef = {
  id: 'w2-fincas-peak-01',
  kind: 'pattern',
  phase: 'peak',
  length: 73,
  columns: [
    column(1.5, 8.5),
    column(10.0, 6.0),
    column(18.5, 9.0),
    column(27.0, 6.5),
    column(35.5, 4.5),
    column(44.0, 7.5),
    column(52.5, 5.0),
    column(61.0, 8.0),
    column(69.5, 8.5),
  ],
  enemies: [searchBloc(16.5, 11.2), paraglider(50.0, 11.5), searchBloc(60.0, 11.2)],
  pickups: [
    dollar(21.75, 7.5),
    dollar(22.75, 7.8),
    dollar(23.75, 7.5),
    dollar(47.25, 6.0),
    dollar(48.25, 6.3),
    dollar(49.25, 6.0),
  ],
};

const peak02: SegmentDef = {
  id: 'w2-fincas-peak-02',
  kind: 'pattern',
  phase: 'peak',
  length: 73,
  columns: [
    column(5.0, 6.0),
    column(13.5, 9.0),
    column(22.0, 6.5),
    column(30.5, 4.5),
    column(39.0, 7.5),
    column(47.5, 5.0),
  ],
  enemies: [gull(20.0, 10.5), searchBloc(30.0, 11.2)],
  pickups: [
    dollar(16.75, 7.5),
    dollar(17.75, 7.8),
    dollar(18.75, 7.5),
    dollar(42.25, 6.0),
    dollar(43.25, 6.3),
    dollar(44.25, 6.0),
  ],
};

/** The arena of JEFE-2: no gap columns outside the portrait walls of its final phase; the fight lasts until the boss retreats (§9.1). */
const arena: SegmentDef = { id: 'w2-fincas-arena-01', kind: 'boss-arena', length: 600, columns: [], enemies: [], pickups: [] };

/** The sky of New York (§11.1): 120 u of free flight with bonus bills. */
const usSky: SegmentDef = {
  id: 'w2-fincas-us-sky-01',
  kind: 'us-sky',
  length: 120,
  columns: [],
  enemies: [],
  pickups: [dollar(20, 6), dollar(21, 6.4), dollar(22, 6.8), dollar(60, 8), dollar(61, 8.4), dollar(62, 8.8), dollar(100, 6), dollar(101, 5.6), dollar(102, 5.2)],
};

/** The jump zone of El Salto (§11.1 steps 2-6): the X on the ground and room for the fall. */
const jumpZone: SegmentDef = { id: 'w2-fincas-jump-01', kind: 'jump', length: 60, columns: [], enemies: [], pickups: [] };

export const OP_203_SEGMENTS: readonly SegmentDef[] = [takeoff, warmup01, tension01, tension02, respite01, peak01, peak02, arena, usSky, jumpZone];

export const OP_203: LevelDef = {
  id: '2-3',
  op: 'OP-203',
  name: 'Las Fincas',
  world: 2,
  scrollSpeed: 6.0,
  timeOfDay: 'dusk',
  segments: OP_203_SEGMENTS.map((segment) => segment.id),
  checkpoints: [
    { id: 'CP-203-A', x: 200 },
    { id: 'CP-203-B', x: 395 },
  ],
  ending: 'boss-and-jump',
  bossId: 'JEFE-2',
};
